/*
 * Gourmet Easy reviews track. Reads real review data out of Judge.me's
 * own featured-carousel widget (rendered hidden, see ge-reviews.liquid)
 * and renders it into our own simple horizontal track -- avoids fighting
 * Judge.me's own carousel-sizing JS/CSS, which produced a single
 * clipped tile when the widget was restyled in place.
 */
(() => {
  /*
   * Two different defects used to share one test, and only one of them was
   * real.
   *
   * The real one: Judge.me's cache has lost some of its punctuation and
   * accents. Between a letter and a contraction, U+FFFD stands in for an
   * apostrophe ("doesn__t") and CONTRACTION repairs it. A "?" *inside* a
   * word is a lost accented letter -- "Der Sch?ler", "qualit?" -- and that
   * is not repairable, because nothing in the text says which letter it was.
   * Those reviews are still withheld: putting visibly corrupted words on the
   * page serves nobody, least of all the customer who wrote them.
   *
   * The one that was not real: language. The old test also dropped every
   * review carrying a German, French, Spanish, Italian or Dutch word, which
   * removed roughly half the corpus -- including reviews that were perfectly
   * legible ("Sehr gut", "ottimo prodotto", "Bonne qualite"). Those are back.
   * They were already being syndicated to Google by the review feed, so the
   * storefront had been the only place they were hidden.
   *
   * Measured 2026-09-13 across four products, 382 reviews: 31 carry the
   * mid-word "?" and are still held back; 85 are non-English and now show.
   */
  const CONTRACTION = /([A-Za-z])\uFFFD+(?=(?:s|t|m|d|re|ve|ll)\b)/g;
  // Two unambiguous shapes. A "?" between letters ("Sch?ler") and a "?"
  // opening a word ("?pluche" for Eplucheur) are both lost accents: real
  // question marks sit at the END of a word, never inside or in front of one.
  // Two more, added 2026-09-25 after scanning 3,554 reviews the filter let
  // through: a "?" ENDING a word with a lowercase word after it ("gro? zu",
  // "Mu? fur" -- a lost ß or u; a real question is followed by a capital), and
  // any U+FFFD left once CONTRACTION has repaired the apostrophes. cleanText
  // used to delete those silently, which printed "splmaschinengeeignet" and
  // "D'o son" -- still corrupted, just less obviously.
  const MANGLED = /[A-Za-z]\?[A-Za-z]|(?:^|\s)\?[A-Za-z]|[A-Za-z]\?\s+[a-z]|\uFFFD/;

  // Not a language test: the text is unreadable whatever language it is in.
  function isMangled(text) {
    return MANGLED.test(text.replace(CONTRACTION, "$1'"));
  }


  // Repair the apostrophes and drop the "??" that corrupted emoji leave
  // behind -- anywhere a run of them stands on its own ("Great product ????",
  // "open for bribes ??)"), not only at the end. A run attached to a word
  // ("klebrig??") is a real question and stays. Any other U+FFFD is left in
  // place for isMangled to reject rather than deleted into a misspelling.
  function cleanText(text) {
    return text
      .replace(CONTRACTION, "$1'")
      .replace(/(^|\s)\?{2,}(?=\s|$|[).,!])/g, '$1')
      .replace(/[ \t]{2,}/g, ' ')
      .replace(/\s+\)/g, ')')
      .trim();
  }

  function starCount(ratingEl) {
    if (!ratingEl) return 5;
    const label = ratingEl.getAttribute('aria-label') || '';
    const match = label.match(/[\d.]+/);
    if (match) return Math.round(parseFloat(match[0]));
    return ratingEl.querySelectorAll('.jdgm-star.jdgm--on').length || 5;
  }

  function buildCard(item) {
    const name = item.querySelector('.jdgm-carousel-item__reviewer-name');
    const rating = item.querySelector('.jdgm-carousel-item__review-rating');
    const title = item.querySelector('.jdgm-carousel-item__review-title');
    const body = item.querySelector('.jdgm-carousel-item__review-body');
    const timestamp = item.querySelector('.jdgm-carousel-item__timestamp');

    if (!body) return null;

    const titleText = cleanText(title ? title.textContent.trim() : '');
    const bodyText = cleanText(body.textContent.trim());
    // Empty once the corrupted emoji are stripped ("?? ??"): nothing to show.
    if (!bodyText || isMangled(`${titleText} ${bodyText}`)) return null;

    const li = document.createElement('li');
    li.className = 'ge-reviews__card';

    const top = document.createElement('div');
    top.className = 'ge-reviews__card-top';

    const nameEl = document.createElement('span');
    nameEl.className = 'ge-reviews__name';
    nameEl.textContent = name ? name.textContent.trim() : 'Verified buyer';
    top.appendChild(nameEl);

    const starsEl = document.createElement('span');
    starsEl.className = 'ge-reviews__stars';
    starsEl.textContent = '★★★★★'.slice(0, starCount(rating));
    top.appendChild(starsEl);

    li.appendChild(top);

    // The design names the product on each card. Judge.me's carousel emits
    // .jdgm-carousel-item__product empty for this shop -- there is no product
    // title, link or id anywhere in the item -- so there is nothing to name.

    if (titleText) {
      const titleEl = document.createElement('p');
      titleEl.className = 'ge-reviews__title';
      titleEl.textContent = titleText;
      li.appendChild(titleEl);
    }

    const bodyWrap = document.createElement('div');
    bodyWrap.className = 'ge-reviews__text-wrap';
    const bodyEl = document.createElement('p');
    bodyEl.className = 'ge-reviews__text';
    bodyEl.textContent = bodyText;
    bodyWrap.appendChild(bodyEl);
    li.appendChild(bodyWrap);

    if (timestamp) {
      const dateEl = document.createElement('div');
      dateEl.className = 'ge-reviews__loc';
      dateEl.textContent = timestamp.getAttribute('data-time') || timestamp.textContent.trim();
      li.appendChild(dateEl);
    }

    return li;
  }

  function init(root) {
    const source = root.querySelector('[data-ge-reviews-source]');
    const track = root.querySelector('[data-ge-reviews-track]');
    const prev = root.querySelector('[data-ge-reviews-prev]');
    const next = root.querySelector('[data-ge-reviews-next]');
    if (!source || !track) return;

    // A <template> (see ge-reviews.liquid) keeps its markup in .content.
    const items = (source.content || source).querySelectorAll('.jdgm-carousel-item');
    let count = 0;

    items.forEach((item) => {
      const card = buildCard(item);
      if (card) {
        track.appendChild(card);
        count += 1;
      }
    });

    if (count === 0) {
      root.style.display = 'none';
      return;
    }

    const scrollAmount = () => Math.min(track.clientWidth * 0.9, 640);

    /*
     * The arrows scroll smoothly, which takes a few hundred ms. The drift
     * loop would otherwise write its own position over the top of that on
     * its next step and swallow the jump, so hold it for the duration and
     * let it pick the new position up when it resumes.
     */
    let drift = null;
    const nudge = (direction) => {
      if (drift) drift.hold();
      track.scrollBy({ left: direction * scrollAmount(), behavior: 'smooth' });
      if (drift) drift.releaseSoon();
    };

    if (prev) prev.addEventListener('click', () => nudge(-1));
    if (next) next.addEventListener('click', () => nudge(1));

    drift = autoScroll(root, track, count);
  }

  /*
   * Continuous drift at the same pixels-per-second as the marquee strip
   * above. ge-marquee.js publishes that figure on the marquee element,
   * because once it clones the strip to fill the screen the speed can no
   * longer be derived from the duration setting alone.
   */
  function autoScroll(root, track, count) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (count < 2) return;

    const marquee = document.querySelector('.ge-marquee[data-ge-px-per-second]');
    const speed = marquee ? parseFloat(marquee.dataset.gePxPerSecond) : 36;
    if (!speed || speed <= 0) return;

    /*
     * A second copy of the cards, so reaching the end is handled by
     * subtracting one copy's width rather than snapping to zero -- the jump
     * lands on an identical card and is invisible.
     */
    [...track.children].forEach((card) => track.appendChild(card.cloneNode(true)));

    // The stylesheet sets smooth scrolling for the arrow buttons; per-frame
    // writes have to be instant or they queue and fight each other.
    track.style.scrollBehavior = 'auto';

    /*
     * The loop owns `position` and only writes it. Reading scrollLeft forces
     * the browser to sample any running CSS animation on the main thread
     * rather than leaving it on the compositor, so the one read needed -- to
     * accept wherever the visitor moved the track -- is taken on the way out
     * of a pause, not every step. Every way the track can be moved by hand
     * pauses the drift first: hover, keyboard focus, a touch gesture, or an
     * arrow press via hold()/releaseSoon().
     */
    let interacting = false; // hover, keyboard focus or a touch gesture
    let nudging = false; // an arrow-button scroll is in flight
    let hidden = false;
    let wasPaused = false;

    root.addEventListener('mouseenter', () => {
      interacting = true;
    });
    root.addEventListener('mouseleave', () => {
      interacting = false;
    });
    root.addEventListener('focusin', () => {
      interacting = true;
    });
    root.addEventListener('focusout', () => {
      interacting = false;
    });

    // Touch has no hover to pause on, so bracket the gesture itself.
    const touchOn = () => {
      interacting = true;
    };
    const touchOff = () => {
      interacting = false;
    };
    track.addEventListener('touchstart', touchOn, { passive: true });
    track.addEventListener('touchend', touchOff, { passive: true });
    track.addEventListener('touchcancel', touchOff, { passive: true });

    document.addEventListener('visibilitychange', () => {
      hidden = document.hidden;
    });

    let nudgeTimer;
    const hold = () => {
      nudging = true;
    };
    const releaseSoon = () => {
      clearTimeout(nudgeTimer);
      // Long enough for a smooth scrollBy to settle. Erring long is
      // harmless: the drift is paused, not broken.
      nudgeTimer = setTimeout(() => {
        nudging = false;
      }, 700);
    };

    /*
     * Position is kept as a float here rather than read back from
     * scrollLeft each frame. scrollLeft rounds to whole pixels, so a
     * 0.6px-per-frame step became a 1px step and the track drifted at
     * 60px/s against the marquee's 36 -- visibly faster than the strip it
     * is supposed to match.
     */
    let position = track.scrollLeft;
    let last = null;

    // scrollWidth forces layout and only changes on resize, so it is
    // measured once here rather than per frame.
    let half = track.scrollWidth / 2;
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        half = track.scrollWidth / 2;
      }, 200);
    });

    let rafId = null;

    /*
     * Step at 30fps. Writing scrollLeft dirties layout, so halving the steps
     * halves the loop's cost; at 36px/s a step moves ~1.2px, which does not
     * read differently from 60fps.
     *
     * The threshold sits a shade under 1/30s on purpose: two 60Hz frames come
     * to 33.33ms, which would land exactly on 1/30 and tip into skipping a
     * third frame (20fps). `last` is left alone on a skipped frame, so dt
     * accumulates and distance stays a function of elapsed time, not frames.
     */
    const MIN_STEP = 1 / 30 - 0.004;

    const tick = (now) => {
      if (last === null) last = now;
      const dt = (now - last) / 1000;

      if (dt < MIN_STEP) {
        rafId = window.requestAnimationFrame(tick);
        return;
      }
      last = now;

      if (interacting || nudging || hidden) {
        wasPaused = true;
        rafId = window.requestAnimationFrame(tick);
        return;
      }

      /*
       * The one read, taken only on the transition back to drifting: whatever
       * the visitor did while we were paused, this is where we accept it.
       */
      if (wasPaused) {
        position = track.scrollLeft;
        wasPaused = false;
      }

      // Absurd dt is ignored -- a backgrounded tab resuming would otherwise
      // jump the track by however many seconds elapsed.
      if (dt > 0 && dt < 0.5 && half > 0) {
        position += speed * dt;
        if (position >= half) position -= half;
        track.scrollLeft = position;
      }
      rafId = window.requestAnimationFrame(tick);
    };

    /*
     * Run only while the section is on screen -- the difference between a
     * loop that idles and one that does not exist. `last` is reset on each
     * start so the first frame after a restart does not see a multi-second
     * dt and jump the track.
     */
    const start = () => {
      if (rafId !== null) return;
      last = null;
      rafId = window.requestAnimationFrame(tick);
    };
    const stop = () => {
      if (rafId === null) return;
      window.cancelAnimationFrame(rafId);
      rafId = null;
    };

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => (e.isIntersecting ? start() : stop()));
        },
        { rootMargin: '200px 0px' }
      ).observe(root);
    } else {
      start();
    }

    return { hold, releaseSoon };
  }

  function initAll() {
    document.querySelectorAll('[data-ge-reviews]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
